import { getMarketCode } from "./get-market-code";

// Shared by the browser (CAPI match keys) and the API (check-first-purchase),
// so this module must stay free of browser-only imports.

// E.164 country dialling codes, and the max length of a *local* subscriber
// number once the trunk `0` is gone. The length is what disambiguates
// "already country-coded" from "local number that happens to start with the
// dialling code" -- e.g. the SG landline 65123456 is 8 local digits that
// merely begin with 65.
var MARKET_PHONE_FORMATS = {
  AU: {
    dial: "61",
    localMax: 9
  },
  NZ: {
    dial: "64",
    localMax: 9
  },
  SG: {
    dial: "65",
    localMax: 8
  }
};

/**
 * Billing phone -> E.164 digits (`61400111222`), for identity match keys.
 *
 * Meta hashes the phone exactly as it arrives, so it has to be normalised
 * before it leaves the browser. Segment's Conversions API destination only
 * does `phone.replace(/\D/g, '')` and then SHA-256 — it never strips the
 * trunk `0` and cannot infer a country code. So a local `0400 111 222`
 * hashes as `0400111222` while Meta hashes `61400111222`, and the two can
 * never match. Once hashed it's unfixable downstream.
 *
 * Deliberately NOT `normalizePhoneNumber` from form-fields/Masks — that is a
 * display formatter that goes the other way (`61…` -> `0…`, plus spaces).
 */
export var toE164Digits = function toE164Digits(raw) {
  var _MARKET_PHONE_FORMATS;
  var digits = String(raw !== null && raw !== void 0 ? raw : "").replace(/\D/g, "");
  if (!digits) return "";
  var _ref = (_MARKET_PHONE_FORMATS = MARKET_PHONE_FORMATS[getMarketCode()]) !== null && _MARKET_PHONE_FORMATS !== void 0 ? _MARKET_PHONE_FORMATS : MARKET_PHONE_FORMATS.AU,
    dial = _ref.dial,
    localMax = _ref.localMax;

  // A leading zero is the national trunk prefix and is never part of E.164.
  if (digits.startsWith("0")) return dial + digits.replace(/^0+/, "");

  // Already country-coded (length-checked, per the note above).
  if (digits.startsWith(dial) && digits.length > localMax) return digits;

  // Bare local number, including the AU "dropped the leading 0" shape.
  return dial + digits;
};

// AU landline area codes by state, keyed by lowercased state name AND code so
// either the BC stateOrProvince ("Victoria") or stateOrProvinceCode ("VIC")
// resolves. Mirrors Rails AU_AREA_CODES exactly.
var AU_AREA_CODES = {
  "nsw": "02",
  "new south wales": "02",
  "act": "02",
  "australian capital territory": "02",
  "vic": "03",
  "victoria": "03",
  "tas": "03",
  "tasmania": "03",
  "qld": "07",
  "queensland": "07",
  "wa": "08",
  "western australia": "08",
  "sa": "08",
  "south australia": "08",
  "nt": "08",
  "northern territory": "08"
};
var auAreaCodeForState = function auAreaCodeForState(state) {
  var _AU_AREA_CODES$String;
  return (_AU_AREA_CODES$String = AU_AREA_CODES[String(state !== null && state !== void 0 ? state : "").trim().toLowerCase()]) !== null && _AU_AREA_CODES$String !== void 0 ? _AU_AREA_CODES$String : "";
};

/**
 * AU billing phone -> national `0` + 9 digits (`0400111222`), the exact form the
 * Rails order sync stores in addresses.telephone via to_national. Used only as
 * an identity match key for get_order_count, NOT for display or CAPI.
 *
 * An 8-digit landline missing its area code is recovered from the billing
 * address `state` (mirrors Rails local_eight_digits + area_code_for_state).
 * Anything that still can't resolve to a valid AU number -> "" — fail safe to
 * the email-only lookup rather than guess and false-match a stranger.
 */
var toAuNational = function toAuNational(raw, state) {
  var digits = String(raw !== null && raw !== void 0 ? raw : "").replace(/\D/g, "");
  if (!digits) return "";

  // 8 local digits, bare or with a 61 country code, = a landline subscriber
  // number missing its area code.
  var eightLocal = /^\d{8}$/.test(digits) ? digits : /^61\d{8}$/.test(digits) ? digits.slice(2) : "";
  var national = "";
  if (digits.startsWith("61") && digits.length === 11) {
    national = "0" + digits.slice(2); // +61 country code -> trunk 0
  } else if (digits.startsWith("0") && digits.length === 10) {
    national = digits; // already national
  } else if (digits.length === 9) {
    national = "0" + digits; // dropped the leading 0
  } else if (eightLocal) {
    // Missing area code -> recover it from the shipping state (mirrors Rails
    // local_eight_digits + area_code_for_state). No state -> don't guess.
    var area = auAreaCodeForState(state);
    if (!area) return "";
    national = area + eightLocal; // e.g. "03" + 8 digits
  } else {
    return "";
  }

  // AU mobiles/geographic: 0 + [234578] + 8 digits. Mirrors Rails valid_au_phone?.
  return /^0[234578]\d{8}$/.test(national) ? national : "";
};

/**
 * Billing phone -> the market's stored canonical, for the get_order_count phone
 * match. AU: national `0` + 9 digits. SG: bare 8-digit local subscriber number
 * (no 65 prefix — the form SG addresses.telephone is actually stored in). Any
 * other market / any unparseable input -> "" so the lookup silently falls back
 * to email-only.
 */
export var toAdminMatchPhone = function toAdminMatchPhone(raw, state) {
  var marketCode = getMarketCode();
  if (marketCode === "AU") return toAuNational(raw, state);
  if (marketCode === "SG") {
    // Reuse the trap-correct E.164 parser, then drop the 65 country code back to
    // the bare local number Rails format_phone_sg stores. (toE164Digits maps any
    // SG shape — bare/65-prefixed/+65 — to 65 + local, so stripping one leading
    // 65 is safe and round-trips a local landline beginning 65.)
    var local = toE164Digits(raw).replace(/^65/, "");
    return /^[3689]\d{7}$/.test(local) ? local : "";
  }
  return "";
};